import React from 'react'
import moment from 'moment'
import { useBuildInfo } from '../hooks/build-info'
import { IconCheck, IconCopy } from '../icon/icon'
import { useCopy } from '../clipboard/clipboard'
import { ManualCopy } from '../clipboard/manual-copy'

// Environments worth naming. DEFAULT is the unconfigured fallback and says
// nothing useful, so it earns no chip rather than printing the word "default"
// next to a version.
const ENV_CHIP = {
  DEV: { label: 'Dev', className: 'bg-warning-subtle text-warning-emphasis' },
  PROD: { label: 'Prod', className: 'bg-success-subtle text-success-emphasis' },
  LOCAL: { label: 'Local', className: 'bg-secondary-subtle text-secondary-emphasis' }
}

/**
 * Which build am I on, at the foot of the account menu.
 *
 * Answers the question that cost real confusion: a tab keeps running whatever it
 * downloaded, so "is this the fix I just deployed?" has no answer anywhere in the
 * UI. The content hash is the honest identity — it is what the update check
 * compares, and what distinguishes two deploys of the same version number.
 *
 * Deliberately NOT where "a newer build is available" is announced. That belongs
 * across the page where it cannot be missed; this answers a different question,
 * and answers it whether or not anything newer exists.
 */
export const BuildInfo = () => {
  const { version, env, hash, releasedAt } = useBuildInfo()
  const copy = useCopy()

  // Nothing identifying to show (dev server, no hash) — render nothing rather
  // than a line that says "EducationXR Admin" and no more.
  if (!version && !hash) return null

  const chip = ENV_CHIP[(env || '').toUpperCase()]
  const released = releasedAt ? moment(releasedAt).fromNow() : null

  const copyText = [
    version ? `EducationXR Admin ${version}` : 'EducationXR Admin',
    chip ? chip.label : null,
    hash ? `build ${hash}` : null,
    released ? `released ${released}` : null
  ]
    .filter(Boolean)
    .join(' · ')

  // Inside the click (shared/clipboard), so Safari allows it. "Copied" only
  // after a real copy; otherwise the details are shown selected.
  const handleCopy = () => copy.copy(copyText)

  return (
    <div className="border-top p-2">
      <div className="bg-body-tertiary border rounded-3 px-2 py-2">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold small">
            {version ? `EducationXR Admin ${version}` : 'EducationXR Admin'}
          </span>
          {chip &&
            <span className={`badge rounded-1 ${chip.className}`} style={{ fontSize: '.62rem', letterSpacing: '.06em' }}>
              {chip.label}
            </span>}
        </div>

        <div className="text-muted mt-1 d-flex align-items-center flex-wrap gap-1" style={{ fontSize: '.74rem' }}>
          {hash && <span className="font-monospace">{hash}</span>}
          {hash && released && <span className="opacity-50">·</span>}
          {/* The absolute time on hover: "5 days ago" is the right answer at a
              glance and the wrong one when someone is comparing two deploys. */}
          {released && <span title={moment(releasedAt).format('LLL')}>released {released}</span>}

          {/* Next to the text it copies, not off in the corner — it acts on this
              line, so it belongs at the end of it. */}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 ms-1 text-muted text-decoration-none lh-1"
            onClick={handleCopy}
            title={copy.copied ? 'Copied' : 'Copy build details'}
            aria-label="Copy build details"
          >
            {copy.copied ? <IconCheck /> : <IconCopy />}
          </button>
        </div>
        {copy.manual && <ManualCopy text={copyText} className="mt-1" />}
      </div>
    </div>
  )
}
